/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
// Backstage Repo File Location -plugins\org\src\helpers\helpers.ts
import {
  CompoundEntityRef,
  DEFAULT_NAMESPACE,
  Entity,
  GroupEntity,
  RELATION_PARENT_OF,
  stringifyEntityRef,
  UserEntity,
} from '@backstage/catalog-model';
import {
  CatalogApi,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';

export const getMembersFromGroups = async (
  groups: CompoundEntityRef[],
  catalogApi: CatalogApi,
) => {
  const membersList =
    groups.length === 0
      ? { items: [] }
      : await catalogApi.getEntities({
          filter: {
            kind: 'User',
            'relations.memberof': groups.map(group =>
              stringifyEntityRef({
                kind: 'group',
                namespace: group.namespace.toLocaleLowerCase('en-US'),
                name: group.name.toLocaleLowerCase('en-US'),
              }),
            ),
          },
        });

  return membersList.items as UserEntity[];
};

export const getDescendantGroupsFromGroup = async (
  group: GroupEntity,
  catalogApi: CatalogApi,
) => {
  const alreadyQueuedOrExpandedGroupNames = new Map<string, boolean>();
  const groupRef: CompoundEntityRef = {
    kind: group.kind,
    namespace: group.metadata.namespace ?? DEFAULT_NAMESPACE,
    name: group.metadata.name,
  };

  const groupQueue = [groupRef];
  const resultantGroupRefs: CompoundEntityRef[] = [];

  // Continue expanding groups until there are no more
  while (groupQueue.length > 0) {
    const activeGroupRef = groupQueue.shift() as CompoundEntityRef;
    const activeGroup = await catalogApi.getEntityByRef(activeGroupRef);
    alreadyQueuedOrExpandedGroupNames.set(
      stringifyEntityRef(activeGroupRef),
      true,
    );

    const childGroups = getEntityRelations(activeGroup, RELATION_PARENT_OF, {
      kind: 'Group',
    }).filter(
      currentGroup =>
        !alreadyQueuedOrExpandedGroupNames.has(
          stringifyEntityRef(currentGroup),
        ),
    );
    childGroups.forEach(childGroup =>
      alreadyQueuedOrExpandedGroupNames.set(
        stringifyEntityRef(childGroup),
        true,
      ),
    );

    groupQueue.push(...childGroups);
    resultantGroupRefs.push(...childGroups);
  }

  return resultantGroupRefs;
};

export const getAllDesendantMembersForGroupEntity = async (
  groupEntity: GroupEntity,
  catalogApi: CatalogApi,
) =>
  getMembersFromGroups(
    await getDescendantGroupsFromGroup(groupEntity, catalogApi),
    catalogApi,
  );

export const removeDuplicateEntitiesFrom = (
  entityArray: Entity[],
): Entity[] => {
  const seenEntities = new Map<string, boolean>();

  return entityArray.filter(entity => {
    const stringifiedEntity = stringifyEntityRef(entity);
    const isDuplicate = seenEntities.has(stringifiedEntity);

    seenEntities.set(stringifiedEntity, true);
    return !isDuplicate;
  });
};
